import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import TokenIcon from './TokenIcon';

const WelcomeModal: React.FC = () => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (user) {
      // Check if user has seen the welcome modal this session
      const hasSeenThisSession = sessionStorage.getItem('welcome_seen_session');
      if (!hasSeenThisSession) {
        // Small delay for better UX
        setTimeout(() => {
          setIsOpen(true);
          setIsAnimating(true);
        }, 500);
      }
    }
  }, [user]);

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(() => {
      setIsOpen(false);
      // Only store in sessionStorage so it shows again on new sessions/logins
      sessionStorage.setItem('welcome_seen_session', 'true');
    }, 300);
  };

  if (!isOpen) return null;

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-opacity duration-300 ${isAnimating ? 'opacity-100' : 'opacity-0'}`}>
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={handleClose}
      />
      
      {/* Modal with animated border */}
      <div className={`relative w-full max-w-lg transform transition-all duration-300 ${isAnimating ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'}`}>
        {/* Subtle teal border */}
        <div className="absolute -inset-[1px] rounded-2xl bg-gradient-to-r from-teal-500/30 via-teal-400/50 to-teal-500/30" />
        
        {/* Inner container with dark gradient background */}
        <div className="relative rounded-2xl overflow-hidden">
          {/* Dark gradient background */}
          <div className="absolute inset-0 bg-gradient-to-br from-zinc-900 via-zinc-950 to-zinc-900" />
          
          {/* Subtle grain overlay */}
          <div className="absolute inset-0 opacity-20" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
          }} />
          
          {/* Floating orbs - subtle teal tones */}
          <div className="absolute -top-20 -left-20 w-40 h-40 bg-teal-500/10 rounded-full blur-3xl animate-float" />
          <div className="absolute -bottom-20 -right-20 w-60 h-60 bg-teal-500/10 rounded-full blur-3xl animate-float-delayed" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-zinc-800/50 rounded-full blur-3xl" />

          {/* Content */}
          <div className="relative p-8 text-white">
            {/* Close button */}
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 p-2 rounded-full hover:bg-white/10 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Header */}
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-teal-500/20 backdrop-blur-sm mb-4 border border-teal-500/30">
                <svg className="w-8 h-8 text-teal-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold mb-2">Welcome to the Garden!</h2>
              <p className="text-white/60">Your hub for AI-powered productivity</p>
            </div>

            {/* What is this */}
            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 mb-4 border border-white/10">
              <h3 className="font-semibold mb-2 flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
                </svg>
                What is this?
              </h3>
              <p className="text-sm text-white/60">
                A curated garden of AI modules, prompts, automations, and how-to guides designed to supercharge your workflow. Browse, copy, and implement to save hours every week.
              </p>
            </div>

            {/* How to earn tokens */}
            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 mb-6 border border-white/10">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <TokenIcon size="sm" />
                Earn Tokens & Win Rewards
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-xs font-bold">+5</span>
                  <span className="text-white/60">View and explore modules</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-xs font-bold">+10</span>
                  <span className="text-white/60">Copy prompts and content</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-xs font-bold">+25</span>
                  <span className="text-white/60">Mark modules as implemented</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-xs font-bold">+50</span>
                  <span className="text-white/60">Create and share your own modules</span>
                </div>
              </div>
            </div>

            {/* Prizes callout */}
            <div className="bg-white/5 rounded-xl p-4 mb-6 border border-white/20">
              <div className="flex items-start gap-3">
                <span className="text-2xl">🏆</span>
                <div>
                  <h3 className="font-semibold mb-1">Redeem for Real Rewards</h3>
                  <p className="text-sm text-white/60">
                    Cash out your tokens for gift cards, prizes, or cold hard cash! Top contributors get featured and earn bonus rewards.
                  </p>
                </div>
              </div>
            </div>

            {/* CTA Button */}
            <button
              onClick={handleClose}
              className="w-full py-3 px-6 bg-teal-500 text-white font-semibold rounded-xl hover:bg-teal-400 transition-colors flex items-center justify-center gap-2"
            >
              <span>Let's Get Started</span>
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Custom styles for animations */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(10px, -10px) scale(1.05); }
        }
        .animate-float {
          animation: float 8s ease-in-out infinite;
        }
        .animate-float-delayed {
          animation: float 10s ease-in-out infinite;
          animation-delay: -3s;
        }
      `}</style>
    </div>
  );
};

export default WelcomeModal;
